import React, { useState } from 'react';
import { 
  Users,
  Activity,
  CheckCircle2,
  AlertCircle,
  Clock,
  Cpu,
  BarChart3,
  RefreshCw,
  Play,
  Pause,
  Settings,
} from 'lucide-react';
import { Card } from '../../ui/Card'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { cn } from '../../../utils/cn'
import { formatRelativeTime } from '../../../utils/format'

interface AgentNode {
  id: string;
  name: string;
  type: 'coordinator' | 'worker' | 'specialist';
  status: 'active' | 'idle' | 'busy' | 'error';
  load: number;
  tasksCompleted: number;
  region: string;
  capabilities: string[];
  lastSeen: Date;
}

// Mock data
const mockAgents: AgentNode[] = [
  {
    id: 'coord-1',
    name: 'Coordinator Alpha',
    type: 'coordinator',
    status: 'active',
    load: 45,
    tasksCompleted: 1542,
    region: 'us-east-1',
    capabilities: ['orchestration', 'task-distribution'],
    lastSeen: new Date(Date.now() - 30000),
  },
  {
    id: 'worker-1',
    name: 'Worker Node 1',
    type: 'worker',
    status: 'busy',
    load: 78,
    tasksCompleted: 892,
    region: 'us-east-1',
    capabilities: ['data-processing', 'analysis'],
    lastSeen: new Date(Date.now() - 15000),
  },
  {
    id: 'worker-2',
    name: 'Worker Node 2',
    type: 'worker',
    status: 'idle',
    load: 12,
    tasksCompleted: 756,
    region: 'eu-west-1',
    capabilities: ['data-processing', 'ml-inference'],
    lastSeen: new Date(Date.now() - 45000),
  },
  {
    id: 'spec-1',
    name: 'ML Specialist',
    type: 'specialist',
    status: 'active',
    load: 62,
    tasksCompleted: 423,
    region: 'us-west-2',
    capabilities: ['ml-training', 'model-optimization'],
    lastSeen: new Date(Date.now() - 10000),
  },
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active': return 'text-success-500';
    case 'busy': return 'text-warning-500';
    case 'idle': return 'text-gray-500';
    case 'error': return 'text-danger-500';
    default: return 'text-gray-500';
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'active': return 'success';
    case 'busy': return 'warning';
    case 'idle': return 'secondary';
    case 'error': return 'danger';
    default: return 'secondary';
  }
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'coordinator': return '👑';
    case 'worker': return '⚙️';
    case 'specialist': return '🧠';
    default: return '📦';
  }
};

export const MobileDistributedAgentOrchestration: React.FC = () => {
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

  const overviewStats = {
    totalAgents: mockAgents.length,
    activeAgents: mockAgents.filter(a => a.status === 'active' || a.status === 'busy').length,
    totalTasks: mockAgents.reduce((sum, agent) => sum + agent.tasksCompleted, 0),
    avgLoad: Math.round(mockAgents.reduce((sum, agent) => sum + agent.load, 0) / mockAgents.length),
  };

  return (
    <div className="space-y-4">
      {/* Header Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-5 h-5 text-primary-500" />
              <Badge variant="primary" className="text-xs">Active</Badge>
            </div>
            <div className="text-xl font-semibold text-console-text">
              {overviewStats.activeAgents}/{overviewStats.totalAgents}
            </div>
            <div className="text-xs text-console-muted">Agents Online</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-warning-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              {overviewStats.avgLoad}%
            </div>
            <div className="text-xs text-console-muted">Avg Load</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle2 className="w-5 h-5 text-success-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              {overviewStats.totalTasks.toLocaleString()}
            </div>
            <div className="text-xs text-console-muted">Tasks Complete</div>
          </div>
        </Card>

        <Card className="bg-gray-900">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="w-5 h-5 text-accent-500" />
            </div>
            <div className="text-xl font-semibold text-console-text">
              98.2%
            </div>
            <div className="text-xs text-console-muted">Success Rate</div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-gray-900">
        <div className="p-4">
          <h3 className="text-sm font-medium text-console-text mb-3">Orchestration Controls</h3>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="primary" size="sm" className="flex items-center justify-center gap-2">
              <Play className="w-4 h-4" />
              Start All
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Pause className="w-4 h-4" />
              Pause All
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Rebalance
            </Button>
            <Button variant="secondary" size="sm" className="flex items-center justify-center gap-2">
              <Settings className="w-4 h-4" />
              Configure
            </Button>
          </div>
        </div>
      </Card>

      {/* Agent List */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-console-text px-1">Agent Nodes</h3>
        
        {mockAgents.map((agent) => (
          <Card 
            key={agent.id} 
            className={cn(
              "bg-gray-900 cursor-pointer transition-all",
              selectedAgent === agent.id && "ring-2 ring-primary-600"
            )}
            onClick={() => setSelectedAgent(agent.id === selectedAgent ? null : agent.id)}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getTypeIcon(agent.type)}</span>
                  <div>
                    <h4 className="font-medium text-console-text text-sm">{agent.name}</h4>
                    <p className="text-xs text-console-muted">{agent.region}</p>
                  </div>
                </div>
                <Badge variant={getStatusBadge(agent.status)} className="text-xs">
                  {agent.status}
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <div className="text-xs text-console-muted">Load</div>
                  <div className="flex items-center gap-1">
                    <Cpu className={cn(
                      "w-3 h-3",
                      agent.load > 70 ? "text-warning-500" : "text-success-500"
                    )} />
                    <span className="text-sm font-medium text-console-text">{agent.load}%</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-console-muted">Tasks</div>
                  <div className="text-sm font-medium text-console-text">
                    {agent.tasksCompleted}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-console-muted">Last Seen</div>
                  <div className="text-sm font-medium text-console-text">
                    {formatRelativeTime(agent.lastSeen)}
                  </div>
                </div>
              </div>

              {selectedAgent === agent.id && (
                <div className="border-t border-gray-800 pt-3 mt-3">
                  <div className="text-xs text-console-muted mb-2">Capabilities</div>
                  <div className="flex flex-wrap gap-1">
                    {agent.capabilities.map((cap) => (
                      <Badge key={cap} variant="secondary" className="text-xs">
                        {cap}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Task Queue Status */}
      <Card className="bg-gray-900">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-console-text">Task Queue</h3>
            <Clock className="w-4 h-4 text-console-muted" />
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-console-muted">Pending Tasks</span>
              <span className="text-sm font-medium text-console-text">42</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-console-muted">Processing</span>
              <span className="text-sm font-medium text-console-text">12</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-console-muted">Avg Processing Time</span>
              <span className="text-sm font-medium text-console-text">1.2s</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};